
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Briefcase, User, Code, Mail } from 'lucide-react';

const MobileBottomNav: React.FC = () => {
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/', icon: <Home className="w-5 h-5" /> },
    { name: 'Services', path: '/services', icon: <Briefcase className="w-5 h-5" /> },
    { name: 'About', path: '/about', icon: <User className="w-5 h-5" /> },
    { name: 'Projects', path: '/projects', icon: <Code className="w-5 h-5" /> },
    { name: 'Contact', path: '/contact', icon: <Mail className="w-5 h-5" /> },
  ];

  return (
    <motion.div 
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed bottom-0 left-0 right-0 z-50 px-6 pb-8 pt-2 md:hidden pointer-events-none"
    >
      <div className="max-w-md mx-auto relative">
        {/* Floating Dock Container */}
        <div className="bg-zinc-950/90 border border-white/10 backdrop-blur-3xl rounded-full p-1.5 flex justify-between items-center shadow-[0_25px_50px_-12px_rgba(0,0,0,0.8)] pointer-events-auto relative overflow-hidden">
          
          {/* Subtle Scanning Light Effect inside the dock */}
          <motion.div 
            animate={{ x: ["-100%", "200%"] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 w-1/3 h-full bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none"
          />

          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            
            return (
              <Link 
                key={link.path} 
                to={link.path}
                className="relative flex-1 flex flex-col items-center justify-center py-3 transition-all group"
              >
                {/* Active Background Pill */}
                {isActive && (
                  <motion.div 
                    layoutId="mobileNavActivePill"
                    className="absolute inset-1 bg-white/[0.03] rounded-full border border-white/5"
                    transition={{ type: "spring", bounce: 0.25, duration: 0.6 }}
                  />
                )}

                {/* Icon Wrapper */}
                <motion.div
                  animate={{ 
                    y: isActive ? -2 : 0,
                    scale: isActive ? 1.15 : 1,
                    color: isActive ? '#34d399' : '#71717a'
                  }}
                  className="relative z-10 flex flex-col items-center"
                >
                  {link.icon}
                  
                  {/* Active Indicator Dot/Dash */}
                  <AnimatePresence>
                    {isActive && (
                      <motion.div 
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        className="absolute -bottom-2.5 w-1 h-1 bg-accent rounded-full shadow-[0_0_8px_#34d399]"
                      />
                    )}
                  </AnimatePresence>
                </motion.div>

                {/* Label (Visible on small devices if space allows, hidden here for minimalist aesthetic) */}
                <motion.span 
                  animate={{ 
                    opacity: isActive ? 1 : 0,
                    scale: isActive ? 1 : 0.8,
                    y: isActive ? 0 : 4
                  }}
                  className="absolute -bottom-6 text-[8px] font-bold tracking-[0.2em] uppercase text-accent/80 pointer-events-none"
                >
                  {link.name}
                </motion.span>
              </Link>
            );
          })}
        </div>
        
        {/* Glow effect underneath the pill */}
        <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-8 bg-accent/10 blur-2xl rounded-full -z-10 pointer-events-none" />
      </div>
    </motion.div>
  );
};

export default MobileBottomNav;
